import { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function ContactPage() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    fullName: '',
    organization: '',
    projectType: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.fullName || !formData.organization || !formData.projectType || !formData.message) {
      alert(t('contact.form.error'));
      return;
    }

    if (formData.message.length > 500) {
      alert(t('contact.form.charLimit'));
      return;
    }

    setSubmitting(true);

    try {
      const formDataEncoded = new URLSearchParams();
      formDataEncoded.append('fullName', formData.fullName);
      formDataEncoded.append('email', formData.organization);
      formDataEncoded.append('projectType', formData.projectType);
      formDataEncoded.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6ott9mancq2eie82570', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataEncoded.toString()
      });

      if (response.ok) {
        setSuccess(true);
        setFormData({
          fullName: '',
          organization: '',
          projectType: '',
          message: ''
        });
        setTimeout(() => setSuccess(false), 5000);
      }
    } catch (error) {
      alert(t('contact.form.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      {/* Hero Section */}
      <section className="relative bg-[#1B2A4A] text-white py-24">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1B2A4A] via-[#1B2A4A] to-[#2A3F5F] opacity-90"></div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">{t('contact.hero.title')}</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t('contact.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-6">
                {success && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg">
                    {t('contact.form.success')}
                  </div>
                )}

                <div>
                  <label htmlFor="fullName" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('contact.form.name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4A017] focus:border-transparent text-sm"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="organization" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('contact.form.organization')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4A017] focus:border-transparent text-sm"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="projectType" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('contact.form.projectType')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="projectType"
                    name="projectType"
                    value={formData.projectType}
                    onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4A017] focus:border-transparent text-sm"
                    required
                  >
                    <option value="">{t('contact.form.projectType.select')}</option>
                    <option value="institutional">{t('contact.form.projectType.institutional')}</option>
                    <option value="studio">{t('contact.form.projectType.studio')}</option>
                    <option value="other">{t('contact.form.projectType.other')}</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('contact.form.message')} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4A017] focus:border-transparent text-sm resize-none"
                    required
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 {t('contact.form.characters')}</p>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#D4A017] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#B8890F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {submitting ? '...' : t('contact.form.send')}
                </button>
              </form>
            </div>

            {/* Contact Info Sidebar */}
            <div className="space-y-8">
              {/* Contact Information */}
              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-xl font-bold text-[#1B2A4A] mb-6">{t('contact.info.title')}</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#D4A017] text-white rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">{t('contact.info.email')}</p>
                      <a href="mailto:hq@maalow.com" className="text-[#1B2A4A] hover:text-[#D4A017] transition-colors">
                        hq@maalow.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#D4A017] text-white rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">{t('contact.info.address')}</p>
                      <p className="text-[#1B2A4A]">N/75 Hann Maristes 2<br />Dakar, Senegal</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#D4A017] text-white rounded-lg flex-shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">{t('contact.info.hours')}</p>
                      <p className="text-[#1B2A4A]">{t('contact.info.hours.value')}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Legal Compliance */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-sm font-bold text-[#1B2A4A] mb-3">{t('about.legal.title')}</h4>
                <div className="space-y-2 text-xs text-gray-600">
                  <p><strong>{t('about.legal.ninea')}:</strong> 004592847</p>
                  <p><strong>{t('about.legal.rccm')}:</strong> SN.DKR.2025.B.6799</p>
                  <p className="pt-2 border-t border-gray-200">
                    Maalow Consulting Services
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-[#1B2A4A] mb-8 text-center">{t('contact.map.title')}</h2>
          <div className="rounded-lg overflow-hidden shadow-lg" style={{ height: '450px' }}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!3d3858.8234567890123!2d-17.4677!3d14.7167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTTCsDQzJzAwLjEiTiAxN8KwMjgnMDMuNyJX!5e0!3m2!1sen!2ssn!4v1234567890123!5m2!1sen!2ssn"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Maalow Office Location - Dakar, Senegal"
            ></iframe>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}